import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface DashboardPreferenceDefaults {
  showPayrollKpis: boolean;
  showPayrollTrend: boolean;
  showCyclePipeline: boolean;
  showEstatePayroll: boolean;
  showTopOperations: boolean;
  showRecentCycles: boolean;
  showExpenseSection: boolean;
  showExpenseTrend: boolean;
  showEstateCapacity: boolean;
  showProductionKpis: boolean;
  showQuickNavigation: boolean;
}

interface DashboardPreferences extends DashboardPreferenceDefaults {
  setShowPayrollKpis: (value: boolean) => void;
  setShowPayrollTrend: (value: boolean) => void;
  setShowCyclePipeline: (value: boolean) => void;
  setShowEstatePayroll: (value: boolean) => void;
  setShowTopOperations: (value: boolean) => void;
  setShowRecentCycles: (value: boolean) => void;
  setShowExpenseSection: (value: boolean) => void;
  setShowExpenseTrend: (value: boolean) => void;
  setShowEstateCapacity: (value: boolean) => void;
  setShowProductionKpis: (value: boolean) => void;
  setShowQuickNavigation: (value: boolean) => void;
}

const DASHBOARD_DEFAULTS: DashboardPreferenceDefaults = {
  showPayrollKpis: true,
  showPayrollTrend: true,
  showCyclePipeline: true,
  showEstatePayroll: true,
  showTopOperations: true,
  showRecentCycles: true,
  showExpenseSection: true,
  showExpenseTrend: true,
  showEstateCapacity: true,
  showProductionKpis: true,
  showQuickNavigation: true,
};

const DashboardPreferencesContext = createContext<DashboardPreferences>({
  ...DASHBOARD_DEFAULTS,
  setShowPayrollKpis: () => {},
  setShowPayrollTrend: () => {},
  setShowCyclePipeline: () => {},
  setShowEstatePayroll: () => {},
  setShowTopOperations: () => {},
  setShowRecentCycles: () => {},
  setShowExpenseSection: () => {},
  setShowExpenseTrend: () => {},
  setShowEstateCapacity: () => {},
  setShowProductionKpis: () => {},
  setShowQuickNavigation: () => {},
});

const STORAGE_KEY = 'dashboard-preferences';

function load(): DashboardPreferenceDefaults {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw)
      return {
        ...DASHBOARD_DEFAULTS,
        ...(JSON.parse(raw) as Partial<DashboardPreferenceDefaults>),
      };
  } catch {
    // ignore
  }
  return DASHBOARD_DEFAULTS;
}

function save(prefs: DashboardPreferenceDefaults) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function useDashboardPreferences() {
  return useContext(DashboardPreferencesContext);
}

export function DashboardPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [showPayrollKpis, setShowPayrollKpisState] = useState(DASHBOARD_DEFAULTS.showPayrollKpis);
  const [showPayrollTrend, setShowPayrollTrendState] = useState(DASHBOARD_DEFAULTS.showPayrollTrend);
  const [showCyclePipeline, setShowCyclePipelineState] = useState(DASHBOARD_DEFAULTS.showCyclePipeline);
  const [showEstatePayroll, setShowEstatePayrollState] = useState(DASHBOARD_DEFAULTS.showEstatePayroll);
  const [showTopOperations, setShowTopOperationsState] = useState(DASHBOARD_DEFAULTS.showTopOperations);
  const [showRecentCycles, setShowRecentCyclesState] = useState(DASHBOARD_DEFAULTS.showRecentCycles);
  const [showExpenseSection, setShowExpenseSectionState] = useState(DASHBOARD_DEFAULTS.showExpenseSection);
  const [showExpenseTrend, setShowExpenseTrendState] = useState(DASHBOARD_DEFAULTS.showExpenseTrend);
  const [showEstateCapacity, setShowEstateCapacityState] = useState(DASHBOARD_DEFAULTS.showEstateCapacity);
  const [showProductionKpis, setShowProductionKpisState] = useState(DASHBOARD_DEFAULTS.showProductionKpis);
  const [showQuickNavigation, setShowQuickNavigationState] = useState(DASHBOARD_DEFAULTS.showQuickNavigation);

  const current = useRef<DashboardPreferenceDefaults>(DASHBOARD_DEFAULTS);

  useEffect(() => {
    const stored = load();
    current.current = stored;
    setShowPayrollKpisState(stored.showPayrollKpis);
    setShowPayrollTrendState(stored.showPayrollTrend);
    setShowCyclePipelineState(stored.showCyclePipeline);
    setShowEstatePayrollState(stored.showEstatePayroll);
    setShowTopOperationsState(stored.showTopOperations);
    setShowRecentCyclesState(stored.showRecentCycles);
    setShowExpenseSectionState(stored.showExpenseSection);
    setShowExpenseTrendState(stored.showExpenseTrend);
    setShowEstateCapacityState(stored.showEstateCapacity);
    setShowProductionKpisState(stored.showProductionKpis);
    setShowQuickNavigationState(stored.showQuickNavigation);
  }, []);

  const setShowPayrollKpis = useCallback((value: boolean) => {
    current.current = { ...current.current, showPayrollKpis: value };
    save(current.current);
    setShowPayrollKpisState(value);
  }, []);

  const setShowPayrollTrend = useCallback((value: boolean) => {
    current.current = { ...current.current, showPayrollTrend: value };
    save(current.current);
    setShowPayrollTrendState(value);
  }, []);

  const setShowCyclePipeline = useCallback((value: boolean) => {
    current.current = { ...current.current, showCyclePipeline: value };
    save(current.current);
    setShowCyclePipelineState(value);
  }, []);

  const setShowEstatePayroll = useCallback((value: boolean) => {
    current.current = { ...current.current, showEstatePayroll: value };
    save(current.current);
    setShowEstatePayrollState(value);
  }, []);

  const setShowTopOperations = useCallback((value: boolean) => {
    current.current = { ...current.current, showTopOperations: value };
    save(current.current);
    setShowTopOperationsState(value);
  }, []);

  const setShowRecentCycles = useCallback((value: boolean) => {
    current.current = { ...current.current, showRecentCycles: value };
    save(current.current);
    setShowRecentCyclesState(value);
  }, []);

  const setShowExpenseSection = useCallback((value: boolean) => {
    current.current = { ...current.current, showExpenseSection: value };
    save(current.current);
    setShowExpenseSectionState(value);
  }, []);

  const setShowExpenseTrend = useCallback((value: boolean) => {
    current.current = { ...current.current, showExpenseTrend: value };
    save(current.current);
    setShowExpenseTrendState(value);
  }, []);

  const setShowEstateCapacity = useCallback((value: boolean) => {
    current.current = { ...current.current, showEstateCapacity: value };
    save(current.current);
    setShowEstateCapacityState(value);
  }, []);

  const setShowProductionKpis = useCallback((value: boolean) => {
    current.current = { ...current.current, showProductionKpis: value };
    save(current.current);
    setShowProductionKpisState(value);
  }, []);

  const setShowQuickNavigation = useCallback((value: boolean) => {
    current.current = { ...current.current, showQuickNavigation: value };
    save(current.current);
    setShowQuickNavigationState(value);
  }, []);

  return (
    <DashboardPreferencesContext.Provider
      value={{
        showPayrollKpis,
        showPayrollTrend,
        showCyclePipeline,
        showEstatePayroll,
        showTopOperations,
        showRecentCycles,
        showExpenseSection,
        showExpenseTrend,
        showEstateCapacity,
        showProductionKpis,
        showQuickNavigation,
        setShowPayrollKpis,
        setShowPayrollTrend,
        setShowCyclePipeline,
        setShowEstatePayroll,
        setShowTopOperations,
        setShowRecentCycles,
        setShowExpenseSection,
        setShowExpenseTrend,
        setShowEstateCapacity,
        setShowProductionKpis,
        setShowQuickNavigation,
      }}
    >
      {children}
    </DashboardPreferencesContext.Provider>
  );
}
